import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import LeadForm from "@/components/LeadForm";
import { FadeUp, Overline } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const CONTACT_EMAIL = "vitaliahges@gmail.com";
const WHATSAPP_DISPLAY = "+61 451 176 010";
const WHATSAPP_LINK = "https://wa.me/61451176010";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to the privacy note before sending.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we'll reply as soon as we can.");
      setForm({ name: "", email: "", message: "", consent: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact Vitaliah — Free Australian Study Guidance" description="Get in touch with Vitaliah for free, independent guidance on studying in Australia — universities, scholarships, applications and more." />
      <Navbar />
      <PageHero
        overline="Contact"
        title="Let's talk about your plans."
        subtitle="Questions about universities, scholarships or applications? Send us a message — or start with free guidance below."
      />
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4 space-y-6">
            <FadeUp>
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-card" className="block bg-white border border-border p-8 hover:border-gold hover:-translate-y-1 transition-[transform,border-color] duration-500">
                <Mail className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif text-2xl font-medium text-navy">Email us</h3>
                <p className="mt-2 text-sm text-navy/60">{CONTACT_EMAIL}</p>
              </a>
            </FadeUp>
            <FadeUp delay={0.1}>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-card" className="block bg-white border border-border p-8 hover:border-gold hover:-translate-y-1 transition-[transform,border-color] duration-500">
                <MessageCircle className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h3 className="mt-5 font-serif text-2xl font-medium text-navy">WhatsApp</h3>
                <p className="mt-2 text-sm text-navy/60">{WHATSAPP_DISPLAY}</p>
              </a>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-8">
            <div className="bg-white border border-border p-8 lg:p-12">
              <Overline>Send a message</Overline>
              <form data-testid="contact-form" onSubmit={submit} className="mt-8 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="rounded-none border-input px-4 py-6 focus-visible:ring-gold" />
                  <Input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" className="rounded-none border-input px-4 py-6 focus-visible:ring-gold" />
                </div>
                <Textarea data-testid="contact-message-input" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" rows={6} className="rounded-none border-input px-4 py-4 focus-visible:ring-gold" />
                <div className="flex items-start gap-3">
                  <Checkbox id="cf-consent" data-testid="contact-consent-checkbox" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: v === true })} className="mt-0.5 rounded-none border-navy/40 data-[state=checked]:bg-navy" />
                  <Label htmlFor="cf-consent" className="text-sm text-navy/70 leading-relaxed font-normal cursor-pointer">
                    I agree to Vitaliah storing and responding to my enquiry in line with the privacy policy.
                  </Label>
                </div>
                <button data-testid="contact-submit-button" type="submit" disabled={loading} className="inline-flex items-center gap-3 bg-navy text-cream px-10 py-4 text-sm font-semibold tracking-wide border border-navy hover:bg-gold hover:border-gold hover:text-navy transition-colors duration-300 disabled:opacity-60">
                  {loading ? "Sending..." : "Send Message"} <ArrowUpRight className="h-4 w-4" />
                </button>
              </form>
            </div>
          </FadeUp>
        </div>
      </section>

      <section id="consultation" data-testid="consultation-section" className="py-24 bg-sand border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <FadeUp><Overline>Free consultation</Overline></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-medium tracking-tight text-navy">Prefer a structured start?</h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-5 text-navy/60 leading-relaxed">Tell us about your goals and an advisor will help you map out your Australian study options — free, no obligations.</p>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <div className="bg-white border border-border p-8 lg:p-12">
              <LeadForm />
            </div>
          </FadeUp>
        </div>
      </section>
      <Footer />
    </div>
  );
}
